import { useState } from "react";
import { FileSpreadsheet, FileDown, FileText, Download } from "lucide-react";
import { useDealContext } from "../hooks/useDealContext";
import { useAuth } from "../contexts/AuthContext";
import { useToastContext } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import {
  downloadModelExport,
  downloadArgusExport,
  downloadBovExport,
  downloadPdfReport,
} from "../api/client";

interface ExportCard {
  id: string;
  title: string;
  description: string;
  icon: typeof FileSpreadsheet;
  action: () => Promise<void>;
  color: string;
  adminOnly?: boolean;
}

export default function ExportsPage() {
  const { deal, scenarios } = useDealContext();
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const dealId = deal?.id;
  const scenario = scenarios?.find((s) => s.projection_result) ?? scenarios?.[0];

  const [loading, setLoading] = useState<string | null>(null);
  const toast = useToastContext();

  const handleExport = async (id: string, fn: () => Promise<void>) => {
    setLoading(id);
    try {
      await fn();
    } catch (e) {
      console.error(`Export ${id} failed:`, e);
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Export failed: ${friendlyError(msg)}`);
    } finally {
      setLoading(null);
    }
  };

  const cards: ExportCard[] = [
    {
      id: "bov",
      title: "BOV Workbook",
      description:
        "Broker Opinion of Value workbook in the institutional reference format: Valuation (Conservative / Expected / Aggressive), Assumptions, Pro Forma, DCF, Rent Roll, Lease-Up Schedule, Sensitivity Tables, monthly exhibits, IRR schedules, and lease expirations.",
      icon: FileSpreadsheet,
      color: "forest",
      action: () => downloadBovExport(dealId!, scenario!.id),
    },
    {
      id: "model",
      title: "Excel Model",
      description:
        "Formula-driven workbook with editable assumptions. Change any input and the entire model recalculates. Includes Summary, Cash Flow, Revenue, Expenses, Reimbursements, Returns, Monthly Detail, and Sensitivity sheets.",
      icon: FileSpreadsheet,
      color: "forest",
      action: () => downloadModelExport(dealId!, scenario!.id),
    },
    {
      id: "cashflow",
      title: "Cash Flow Report",
      description:
        "Pre-formatted Excel report of the annual cash flow waterfall, suitable for printing or attaching to offering memos. Static values, no formulas.",
      icon: FileText,
      color: "forest",
      action: () => downloadArgusExport(dealId!, scenario!.id),
    },
    {
      id: "pdf",
      title: "Executive Summary (PDF)",
      description:
        "One-page investment summary with key metrics, property overview, rent roll snapshot, and cash flow highlights. Ready to share with investors.",
      icon: FileDown,
      color: "forest",
      action: () => downloadPdfReport(dealId!, scenario?.id),
    },
  ];

  const visibleCards = cards.filter((c) => !c.adminOnly || isAdmin);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="font-display text-xl font-semibold text-bark-800">
          Exports
        </h2>
        <p className="text-sm text-bark-400 mt-1">
          Download the underwriting model and reports for{" "}
          <span className="font-medium text-bark-600">
            {scenario?.name || "Base Case"}
          </span>
        </p>
      </div>

      {!scenario ? (
        <div className="bg-grove-50 border border-grove-300 rounded-xl p-8 text-center">
          <Download className="w-8 h-8 text-bark-300 mx-auto mb-3" />
          <p className="text-bark-500 text-sm">
            Run a projection first to enable exports.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {visibleCards.map((card) => {
            const Icon = card.icon;
            const isLoading = loading === card.id;
            return (
              <div
                key={card.id}
                className="bg-white border border-grove-300 rounded-xl shadow-sm overflow-hidden flex flex-col"
              >
                <div className="p-5 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-forest-100 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-forest-600" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-bark-800 mb-1.5">
                    {card.title}
                  </h3>
                  <p className="text-xs text-bark-400 leading-relaxed">
                    {card.description}
                  </p>
                </div>
                <div className="px-5 pb-5">
                  <button
                    onClick={() => handleExport(card.id, card.action)}
                    disabled={isLoading || !scenario}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-forest-700 rounded-lg hover:bg-forest-600 transition-all disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
